'use client'

import { Container } from '@/shared/components/Container'
import { ReactNode, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import Counter from '@/shared/components/framer/Counter'

type Tiers = [
  Array<string>, // 20   - 100
  Array<string>, // 100  - 300
  Array<string>, // 300  - 600
  Array<string>, // 600  - 100
  Array<string>, // 1000 - 2000
  Array<string>, // > 2000 (mostly just a multiple of 1000 - 2000 tiers)
]

type Props = {
  tiers: Tiers
}

export default function SkipTheWaitCalculator({ tiers }: Props) {
  const [times, setTimes] = useState(7)
  const [duration, setDuration] = useState(60)

  const absoluteMinutesSaved = times * (duration - 10)

  let quantitySaved = absoluteMinutesSaved
  let quantifier = 'Minutes'
  if (quantitySaved > 10000) {
    quantitySaved = quantitySaved / 60
    quantifier = 'Hours'
  }

  return (
    <div className="w-full bg-gray-200">
      <Container className="my-16 grid grid-cols-4 gap-x-12 gap-y-8 md:grid-cols-8 xl:grid-cols-12 xl:gap-y-10">
        <div className="col-span-4 md:col-span-5 xl:col-span-8">
          <h2 className="font-bitter text-xl font-semibold text-gray-900 md:text-2xl xl:text-3xl">
            Skip The Wait Calculator
          </h2>
          <p className="text-md mt-3 font-sans text-gray-600 xl:text-lg">
            How much time can you save using NextClinic instead of going to a
            physical clinic for your healthcare needs?
          </p>

          <div className="mt-8 grid grid-cols-1 gap-x-8 xl:grid-cols-2">
            <TextInput
              name="gp-appt-per-year"
              label="How many GP appointment do you need per year?"
              value={times}
              setValue={(t) => setTimes(t)}
              note={
                <>
                  On average, a person needs 7 appointments per year.
                  <sup>1</sup>
                </>
              }
            />
            <TextInput
              name="gp-avg-duration"
              label="How long is your typical GP appointment?"
              suffix="minutes"
              value={duration}
              setValue={(d) => setDuration(d)}
              note={
                <>
                  On average, a patient get the care they need from NextClinic
                  doctors in 10 minutes.
                  <sup>2</sup>
                </>
              }
            />
          </div>
        </div>
        <div className="col-span-4 hidden items-center justify-end md:col-span-3 md:flex">
          {absoluteMinutesSaved > 0 && (
            <div className="flex flex-col items-center gap-y-0.5 rounded-2xl border border-gray-200 bg-gray-50 px-16 py-12">
              <p className="font-bitter text-4xl font-semibold text-gray-900">
                <Counter value={quantitySaved} />
              </p>
              <p className="font-sans text-gray-600">
                <AnimatePresence mode="popLayout">
                  <motion.span
                    key={quantifier}
                    initial={{ filter: 'blur(10px)' }}
                    animate={{
                      filter: 'blur(0px)',
                      transition: { ease: 'easeIn' },
                    }}
                    exit={{
                      filter: 'blur(10px)',
                      transition: { ease: 'easeOut' },
                    }}
                  >
                    {quantifier}
                  </motion.span>
                </AnimatePresence>{' '}
                saved
              </p>
            </div>
          )}
        </div>
        <div className="col-span-4 md:col-span-7 xl:col-span-9">
          <TimeSavedResult
            {...{ absoluteMinutesSaved, tiers, quantitySaved, quantifier }}
          />
        </div>
        <div className="col-span-4 mt-8 flex flex-col items-center justify-center gap-y-1 md:col-span-8 xl:col-span-12 xl:mt-12">
          <p className="font-sans text-xs text-gray-600 xl:text-sm">
            1. According to the Department of Health and Aged Care, there were
            6.6 primary care services per population in 2023-2024.
          </p>
          <p className="font-sans text-xs text-gray-600 xl:text-sm">
            2. This is based on the data from 2024-2025, but your actual
            experience might be different depending on the availability of the
            doctors.
          </p>
        </div>
      </Container>
    </div>
  )
}

type TimeSavedResultProps = {
  absoluteMinutesSaved: number
  quantitySaved: number
  quantifier: string
  tiers: Tiers
}

function TimeSavedResult({
  absoluteMinutesSaved,
  quantitySaved,
  quantifier,
  tiers,
}: TimeSavedResultProps) {
  let key = 0 // one of the tiers so the wording is the same
  let tierIdx = 0
  const variants = {
    before: {
      opacity: 0,
    },
    current: {
      opacity: 1,
    },
    after: {
      opacity: 0,
    },
  }

  function render(child: ReactNode) {
    return (
      <AnimatePresence mode="popLayout">
        <motion.div
          key={key}
          className="font-sans text-sm text-gray-700 xl:text-lg"
          initial="before"
          animate="current"
          exit="after"
          variants={variants}
        >
          {child}
        </motion.div>
      </AnimatePresence>
    )
  }

  if (absoluteMinutesSaved > 2000) {
    tierIdx = 5
  } else if (absoluteMinutesSaved > 1000) {
    tierIdx = 4
  } else if (absoluteMinutesSaved > 600) {
    tierIdx = 3
  } else if (absoluteMinutesSaved > 300) {
    tierIdx = 2
  } else if (absoluteMinutesSaved > 100) {
    tierIdx = 1
  } else if (absoluteMinutesSaved > 20) {
    tierIdx = 0
  } else {
    key = 1
    return render(
      <p>
        You are not likely to save significant amount of time by preferring
        NextClinic over physical clinic. But worry not, it is still hard to beat
        the convenience of an online teleconsultation platform!
      </p>,
    )
  }

  return render(
    <div>
      <p>
        By using NextClinic instead of going to a physical clinic, you can save
        up to{' '}
        <span className="text-rose-600">
          <Counter value={quantitySaved} />{' '}
          <AnimatePresence mode="popLayout">
            <motion.span
              key={quantifier}
              initial={{ filter: 'blur(10px)' }}
              animate={{
                filter: 'blur(0px)',
                transition: { ease: 'easeIn' },
              }}
              exit={{
                filter: 'blur(10px)',
                transition: { ease: 'easeOut' },
              }}
            >
              {quantifier.toLowerCase()}
            </motion.span>
          </AnimatePresence>{' '}
        </span>{' '}
        per year. And this is enough for you to -
      </p>
      <ul className="ml-4 mt-2 list-inside list-disc xl:ml-8">
        <AnimatePresence mode="popLayout">
          {tiers[tierIdx].map((item, index) => (
            <motion.li
              layout
              key={`${tierIdx}-${index}`}
              variants={{
                before: { opacity: 0, x: -50, filter: 'blur(10px)' },
                current: {
                  opacity: 1,
                  x: 0,
                  filter: 'blur(0px)',
                  transition: {
                    ease: 'easeIn',
                    duration: 0.5,
                    delay: 0.1 * index,
                  },
                },
                after: {
                  opacity: 0,
                  x: 50,
                  filter: 'blur(10px)',
                  transition: {
                    ease: 'easeOut',
                    duration: 0.5,
                    delay: 0.1 * index,
                  },
                },
              }}
              initial="before"
              animate="current"
              exit="after"
            >
              {item}
            </motion.li>
          ))}
        </AnimatePresence>
      </ul>
    </div>,
  )
}

type TextInputProps = {
  name: string
  label: string
  suffix?: string
  value: number
  setValue: (value: number) => void
  note?: ReactNode
}

function TextInput({
  name,
  label,
  suffix,
  value,
  setValue,
  note,
}: TextInputProps) {
  return (
    <div className="flex flex-col gap-y-2">
      <label
        className="xl:text-md font-sans text-sm text-gray-800"
        htmlFor={name}
      >
        {label}
      </label>
      <div className="mt-0.5 grid grid-cols-1">
        <input
          className="xl:text-md col-start-1 row-start-1 mt-0.5 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-gray-500 focus:outline-none focus:ring-0"
          name={name}
          id={name}
          value={value}
          onChange={(e) => setValue(parseInt(e.target.value))}
          type="number"
          inputMode="numeric"
        />
        <span className="xl:text-md pointer-events-none col-start-1 row-start-1 mr-3 self-center justify-self-end text-sm text-gray-400">
          {suffix}
        </span>
      </div>
      {note && (
        <p className="font-sans text-xs text-gray-600 xl:text-sm">{note}</p>
      )}
    </div>
  )
}
